import { ReactNode, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import {
  LayoutDashboard,
  Users,
  Briefcase,
  FileText,
  Settings,
  LifeBuoy,
  LogOut,
  Menu,
  Globe,
  User,
  Search,
  Bell,
  X,
  Video,
  UserPlus,
  Folder,
} from 'lucide-react';
import { clsx } from 'clsx';
import { useAuth } from '../../contexts/AuthContext';
import Chat from '../Chat';
import { apiFetch } from '../../lib/api';
import { usePushNotifications } from '../../hooks/usePushNotifications';

const NAV_ITEMS = [
  { name: 'Dashboard', path: '/admin', icon: LayoutDashboard },
  { name: 'CRM', path: '/admin/crm', icon: Users },
  { name: 'Operations', path: '/admin/operations', icon: Briefcase },
  { name: 'Finances', path: '/admin/finances', icon: FileText },
  { name: 'CMS', path: '/admin/cms', icon: Globe },
  { name: 'RH', path: '/admin/hr', icon: User },
  { name: 'Reunions', path: '/admin/meetings', icon: Video },
  { name: 'Recrutement', path: '/admin/recruitment', icon: UserPlus },
  { name: 'Documents', path: '/admin/documents', icon: Folder },
  { name: 'Support', path: '/admin/support', icon: LifeBuoy },
  { name: 'Parametres', path: '/admin/settings', icon: Settings },
  { name: 'Mon Compte', path: '/admin/account-settings', icon: User },
];

type NotificationItem = {
  id: string;
  title: string;
  text: string;
  type: 'project' | 'lead' | 'ticket' | 'invoice';
  created_at: string;
};

export default function DashboardLayout({ children }: { children: ReactNode }) {
  const location = useLocation();
  const { user, logout } = useAuth();
  usePushNotifications(!!user);
  const [showNotifications, setShowNotifications] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const profileRole = (user?.profile_role || '').toLowerCase();

  const visibleNavItems = NAV_ITEMS.filter((item) => {
    if (user?.role === 'ADMIN') return true;
    if (user?.role !== 'EMPLOYEE') return false;

    const supportAllowed = ['/admin', '/admin/support', '/admin/meetings', '/admin/documents', '/admin/account-settings'];
    const projectAllowed = ['/admin', '/admin/operations', '/admin/meetings', '/admin/documents', '/admin/account-settings'];

    if (profileRole.includes('support')) return supportAllowed.includes(item.path);
    if (profileRole.includes('project')) return projectAllowed.includes(item.path);
    return ['/admin', '/admin/meetings', '/admin/documents', '/admin/account-settings'].includes(item.path);
  });

  useEffect(() => {
    apiFetch('/api/dashboard/admin')
      .then((res) => res.json())
      .then((data) => setNotifications(data?.notifications || []))
      .catch(() => setNotifications([]));
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
  };

  return (
    <div className="min-h-screen flex bg-slate-50 font-inter text-slate-900 overflow-hidden">
      {isSidebarOpen && <button onClick={() => setIsSidebarOpen(false)} className="fixed inset-0 bg-slate-900/40 z-20 md:hidden" aria-label="Fermer le menu" />}
      <aside className={clsx("w-72 bg-slate-900 text-slate-400 flex flex-col shadow-2xl z-30 fixed md:static inset-y-0 left-0 transition-transform duration-300", isSidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0')}>
        <div className="h-20 flex items-center px-8 border-b border-white/5">
          <img src="/logo.png" alt="Ahime" width={900} height={255} decoding="async" className="block h-9 w-auto object-contain brightness-0 invert" />
        </div>

        <nav className="flex-1 py-8 px-4 space-y-2 overflow-y-auto custom-scrollbar">
          <p className="px-4 text-[10px] font-black text-slate-500 uppercase tracking-[0.2em] mb-4">Menu principal</p>
          {visibleNavItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.name}
                to={item.path}
                className={clsx(
                  'group flex items-center justify-between px-4 py-3.5 rounded-2xl text-sm font-bold transition-all duration-200',
                  isActive ? 'bg-red-600 text-white shadow-lg shadow-red-600/20' : 'hover:bg-white/5 hover:text-white'
                )}
              >
                <div className="flex items-center gap-3">
                  <item.icon className={clsx('w-5 h-5 transition-colors', isActive ? 'text-white' : 'text-slate-500 group-hover:text-white')} />
                  {item.name}
                </div>
                {isActive && <motion.div layoutId="active-pill" className="w-1.5 h-1.5 bg-white rounded-full" />}
              </Link>
            );
          })}
        </nav>

        <div className="p-4 border-t border-white/5">
          <div className="bg-white/5 rounded-[2rem] p-4 mb-4">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-red-600/20 text-red-400 rounded-xl flex items-center justify-center font-bold">{user?.email?.charAt(0).toUpperCase()}</div>
              <div className="min-w-0">
                <p className="text-sm font-bold text-white truncate">{user?.email}</p>
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Administrateur</p>
              </div>
            </div>
            <button onClick={handleLogout} className="flex items-center justify-center gap-2 w-full py-2.5 rounded-xl text-xs font-bold bg-white/5 hover:bg-red-600 hover:text-white transition-all text-slate-400">
              <LogOut className="w-4 h-4" />
              Deconnexion
            </button>
          </div>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0 overflow-hidden relative">
        <header className="h-20 bg-white/80 backdrop-blur-md border-b border-slate-200 flex items-center justify-between px-10 z-20">
          <div className="flex items-center gap-8 flex-1">
            <button onClick={() => setIsSidebarOpen((v) => !v)} className="md:hidden text-slate-500 hover:text-slate-900">
              <Menu className="w-6 h-6" />
            </button>
            <div className="relative max-w-md w-full hidden md:block group">
              <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-red-600 transition-colors" />
              <input type="text" placeholder="Rechercher dans Ahime OS..." className="w-full pl-12 pr-4 py-2.5 rounded-xl bg-slate-100 border-transparent focus:bg-white focus:ring-4 focus:ring-red-500/10 focus:border-red-500 outline-none transition-all text-sm font-medium" />
            </div>
          </div>

          <div className="flex items-center gap-6">
            <div className="relative">
              <button onClick={() => setShowNotifications(!showNotifications)} className="relative p-2 text-slate-400 hover:text-slate-900 transition-colors">
                <Bell className="w-5 h-5" />
                {notifications.length > 0 && <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-red-500 rounded-full border-2 border-white"></span>}
              </button>

              <AnimatePresence>
                {showNotifications && (
                  <motion.div initial={{ opacity: 0, y: 10, scale: 0.95 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 10, scale: 0.95 }} className="absolute right-0 mt-4 w-80 bg-white rounded-[2rem] shadow-2xl border border-slate-100 overflow-hidden z-50">
                    <div className="p-6 border-b border-slate-50 bg-slate-50/50 flex items-center justify-between">
                      <h3 className="font-bold text-slate-900">Notifications</h3>
                      <button onClick={() => setShowNotifications(false)} className="text-slate-400 hover:text-slate-900">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="max-h-96 overflow-y-auto p-4 space-y-2">
                      {notifications.length === 0 && <p className="text-sm text-slate-500 p-2">Aucune notification.</p>}
                      {notifications.map((n) => (
                        <div key={n.id} className="p-4 rounded-2xl hover:bg-slate-50 transition-colors cursor-pointer group">
                          <div className="flex gap-4">
                            <div className={clsx('w-10 h-10 rounded-xl flex items-center justify-center shrink-0', n.type === 'project' && 'bg-emerald-50 text-emerald-600', n.type === 'lead' && 'bg-red-50 text-red-600', n.type === 'ticket' && 'bg-amber-50 text-amber-600', n.type === 'invoice' && 'bg-indigo-50 text-indigo-600')}>
                              {n.type === 'project' ? <Briefcase className="w-5 h-5" /> : null}
                              {n.type === 'lead' ? <Users className="w-5 h-5" /> : null}
                              {n.type === 'ticket' ? <LifeBuoy className="w-5 h-5" /> : null}
                              {n.type === 'invoice' ? <FileText className="w-5 h-5" /> : null}
                            </div>
                            <div>
                              <p className="font-bold text-sm text-slate-900">{n.title}</p>
                              <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">{n.text}</p>
                              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-2">{new Date(n.created_at).toLocaleString('fr-FR')}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
            <div className="h-8 w-px bg-slate-200"></div>
            <div className="flex items-center gap-3 cursor-pointer group">
              <div className="text-right hidden sm:block">
                <p className="text-sm font-bold text-slate-900 group-hover:text-red-600 transition-colors">Admin Panel</p>
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">v2.4.0-stable</p>
              </div>
              <div className="w-10 h-10 bg-slate-100 rounded-xl flex items-center justify-center border border-slate-200 group-hover:border-red-200 transition-all">
                <Settings className="w-5 h-5 text-slate-500 group-hover:text-red-600 transition-colors" />
              </div>
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-10 bg-slate-50/50">
          <AnimatePresence mode="wait">
            <motion.div key={location.pathname} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.3 }}>
              {children}
            </motion.div>
          </AnimatePresence>
        </main>

        <Chat room="admin_general" currentUser={{ name: 'Admin', email: user?.email }} />
      </div>
    </div>
  );
}
